/**
 * The host telling a guest the game has begun, and who it is in it.
 *
 * Three things, and nothing else that could be worked out. Which mode to open, because the host
 * chose it and the guest cannot guess. What seed the world was built from, because every generator
 * in the simulation hangs off it and a guest with the wrong one would see a different bay behind
 * the host's snapshots. And which player id this guest was given, because ids are the host's to
 * hand out: a guest that picked its own would collide with somebody the moment two joined at once.
 *
 * ```text
 * byte 0    the player id this guest has been given
 * byte 1    length of the mode id, bytes
 * byte 2    length of the seed, bytes
 * byte 3+   the mode id, then the seed, both UTF-8
 * ```
 *
 * Reliable and ordered, so it rides the event channel. Losing it would leave a guest sitting in a
 * lobby watching snapshots it has nowhere to put.
 */

import { NetError } from '@/net/session/sessionErrors';

export const START_HEADER_BYTES = 3;

/** Both are short by nature. A length that will not fit a byte is a bug, not a long name. */
const MAX_TEXT_BYTES = 255;

export interface StartMessage {
  /** Which mode to open. Matches a `GameMode.id`. */
  readonly modeId: string;
  readonly seed: string;
  readonly playerId: number;
}

export function encodeStart(message: StartMessage): Uint8Array {
  const encoder = new TextEncoder();
  const modeId = encoder.encode(message.modeId);
  const seed = encoder.encode(message.seed);

  if (modeId.byteLength > MAX_TEXT_BYTES || seed.byteLength > MAX_TEXT_BYTES) {
    throw new NetError('malformedFrame', 'a mode id or a seed longer than 255 bytes is not one');
  }

  const bytes = new Uint8Array(START_HEADER_BYTES + modeId.byteLength + seed.byteLength);
  bytes[0] = message.playerId & 0xff;
  bytes[1] = modeId.byteLength;
  bytes[2] = seed.byteLength;
  bytes.set(modeId, START_HEADER_BYTES);
  bytes.set(seed, START_HEADER_BYTES + modeId.byteLength);
  return bytes;
}

/** Throws rather than defaulting anything: a guessed seed is a world that quietly disagrees. */
export function decodeStart(payload: Uint8Array): StartMessage {
  if (payload.byteLength < START_HEADER_BYTES) {
    throw new NetError('malformedFrame', 'a start message is at least three bytes');
  }

  const playerId = payload[0] ?? 0;
  const modeLength = payload[1] ?? 0;
  const seedLength = payload[2] ?? 0;

  if (payload.byteLength < START_HEADER_BYTES + modeLength + seedLength) {
    throw new NetError('malformedFrame', 'a start message ends before the names it declared');
  }
  if (playerId < 1) {
    throw new NetError(
      'malformedFrame',
      `player id ${String(playerId)} is not one a host hands out`,
    );
  }

  const decoder = new TextDecoder();
  const modeAt = START_HEADER_BYTES;
  const seedAt = modeAt + modeLength;

  return {
    playerId,
    modeId: decoder.decode(payload.subarray(modeAt, seedAt)),
    seed: decoder.decode(payload.subarray(seedAt, seedAt + seedLength)),
  };
}
